import { useOrganization, useUser } from "@clerk/clerk-react";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Smartphone, Terminal } from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { apiFetch, SandbarApiError } from "@/lib/api";

export const Route = createFileRoute("/_authed/device")({
  component: DevicePage,
});

// The CLI authorization flow approves a device login through the Sandbar API
// (same-origin), which mints the operator's Sandbar session for the waiting
// CLI. apiFetch attaches the operator's Clerk session token. The device_code
// never reaches the browser — the operator hand-transcribes the short user_code
// shown in their terminal, which is the anti-phishing control.
const ApproveResponseSchema = z.object({ approved: z.boolean() });

async function approveDevice(userCode: string): Promise<void> {
  try {
    await apiFetch("/auth/device/approve", ApproveResponseSchema, {
      method: "POST",
      body: JSON.stringify({ user_code: userCode }),
    });
  } catch (err) {
    if (err instanceof SandbarApiError && err.status === 401) {
      throw new Error("Your session has expired. Sign in again and retry.");
    }
    throw new Error("That code is invalid or has expired. Check your terminal and try again.");
  }
}

function DevicePage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { organization } = useOrganization();

  const [code, setCode] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);

  const approve = useMutation({
    mutationFn: async () => approveDevice(code.trim()),
    onSuccess: () => {
      setDone(true);
      toast.success("CLI authorized");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to authorize CLI"),
  });

  function onSubmitCode(e: FormEvent) {
    e.preventDefault();
    if (code.trim().length === 0) return;
    setConfirming(true);
  }

  const operatorEmail = user?.primaryEmailAddress?.emailAddress;

  if (done) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <Card className="flex w-full max-w-sm flex-col items-center gap-6 p-10 text-center">
          <Smartphone className="size-10 text-muted-foreground" />
          <div className="space-y-2">
            <h1 className="font-display text-xl">Device authorized</h1>
            <p className="text-sm text-muted-foreground">
              You can return to your terminal — the sandbar CLI is signed in.
            </p>
          </div>
          <Button className="w-full" onClick={() => navigate({ to: "/sites" })}>
            Go to sites
          </Button>
        </Card>
      </div>
    );
  }

  if (confirming) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <Card className="flex w-full max-w-sm flex-col items-center gap-6 p-10 text-center">
          <Terminal className="size-10 text-muted-foreground" />
          <div className="space-y-2">
            <h1 className="font-display text-xl">Authorize CLI</h1>
            <p className="text-sm text-muted-foreground">
              Approve only if you started this from the sandbar CLI. The CLI will sign in as you.
            </p>
            <p className="font-mono text-base tracking-widest">{code.trim()}</p>
            <p className="text-xs text-muted-foreground">
              {operatorEmail ?? "this account"}
              {organization?.name ? ` · ${organization.name}` : ""}
            </p>
          </div>
          <div className="flex w-full flex-col gap-2">
            <Button disabled={approve.isPending} onClick={() => approve.mutate()}>
              {approve.isPending ? "Approving…" : "Approve"}
            </Button>
            <Button
              variant="outline"
              disabled={approve.isPending}
              onClick={() => setConfirming(false)}
            >
              Back
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid min-h-[60vh] place-items-center">
      <Card className="flex w-full max-w-sm flex-col items-center gap-6 p-10 text-center">
        <Terminal className="size-10 text-muted-foreground" />
        <div className="space-y-2">
          <h1 className="font-display text-xl">Authorize CLI</h1>
          <p className="text-sm text-muted-foreground">
            Enter the code shown in your terminal to sign the sandbar CLI in as you.
          </p>
        </div>
        <form className="flex w-full flex-col gap-3" onSubmit={onSubmitCode}>
          <Input
            autoFocus
            autoComplete="off"
            aria-label="Device code"
            placeholder="ABCD-1234"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            className="text-center font-mono tracking-widest"
          />
          <Button type="submit" disabled={code.trim().length === 0}>
            Continue
          </Button>
        </form>
      </Card>
    </div>
  );
}
